import {
  classifyLogseqFailure,
  logseqConnectionFromEnv,
  logseqEnv,
  type LogseqConnection,
  type LogseqProblem,
} from '@/features/local-mcp/logseq-setup';
import {
  useServerConnectionTestQuery,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import type { ServerEntry } from '@littlebird/axon-mcp';

export type LogseqStatus =
  | { kind: 'checking' }
  | { kind: 'connected' }
  | { kind: 'unreachable'; problem: LogseqProblem };

export function useLogseqConfiguration(server: ServerEntry) {
  const update = useUpdateMcpServerMutation();
  const testQuery = useServerConnectionTestQuery(server);
  const { transport } = server;
  const connection = logseqConnectionFromEnv(
    transport.kind === 'http' ? undefined : transport.env
  );

  const status: LogseqStatus = testQuery.isFetching
    ? { kind: 'checking' }
    : testQuery.data?.ok
      ? { kind: 'connected' }
      : {
          kind: 'unreachable',
          problem: testQuery.data
            ? classifyLogseqFailure(testQuery.data)
            : 'connection_failed',
        };

  // The server restarts with the new env, and the changed transport re-runs
  // the connection test
  const saveConnection = (
    changes: Partial<LogseqConnection>,
    onSaved?: () => void
  ) => {
    if (transport.kind === 'http') return;
    update.mutate(
      {
        slug: server.slug,
        changes: {
          transport: {
            ...transport,
            env: {
              ...transport.env,
              ...logseqEnv({ ...connection, ...changes }),
            },
          },
        },
      },
      { onSuccess: onSaved }
    );
  };

  return {
    connection,
    status,
    saving: update.isPending,
    testAgain: () => void testQuery.refetch(),
    saveConnection,
  };
}
